import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * EditorLayout – Farmly canonical 2-column editor shell.
 *
 * Desktop (≥ lg): left content column (1.4fr) + right sticky sidebar (1fr).
 * Mobile (< lg): single column, sidebar stacks below main content.
 *
 * Mental model:
 *   LEFT  = create / edit content
 *   RIGHT = understand outcome + control state
 *
 * Sidebar follows the canonical trio: Preview → Settings → Actions.
 *
 * Usage:
 *   <EditorLayout
 *     header={<h1>Page title</h1>}
 *     main={<EditorContent />}
 *     sidebar={<SidebarContent />}
 *   />
 */

interface EditorLayoutProps {
  /** Optional header row spanning full width above the grid */
  header?: ReactNode;
  /** Main editor content (left column on desktop) */
  main: ReactNode;
  /** Sidebar content – preview, settings, actions (right column, sticky on desktop) */
  sidebar: ReactNode;
  /** Additional class names on root container */
  className?: string;
}

export function EditorLayout({ header, main, sidebar, className }: EditorLayoutProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Full-width header */}
      {header && <div>{header}</div>}

      {/* 2-column grid on desktop, stacked on mobile */}
      <div
        className={cn(
          'space-y-6',
          'lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-x-10 lg:items-start lg:space-y-0',
        )}
      >
        {/* Main content column */}
        <div className="space-y-6 min-w-0">
          {main}
        </div>

        {/* Sidebar column – sticky on desktop */}
        <div className="lg:sticky lg:top-24">
          {sidebar}
        </div>
      </div>
    </div>
  );
}
